import qs from "qs";
import { FilterDef, HTTPGenericResponse } from "../../constants/FinanceModels";
import * as Const from "../../pages/sdd/constants/MandateConstants";
import * as CollConst from "../../pages/sdd/constants/CollectionConstants";
import * as CollDetailConst from "../../pages/sdd/constants/CollectionDetailConstants";
import { getFilterValue } from "../../utils/FilterUtils";
import { useApi } from "../Api";
import dayjs from "dayjs";

export const useSddRepository = () => {
    const { GET, POST, PUT, DELETE } = useApi();

    const PATH = "/finance/sdd"

    async function getMandate(id: string): Promise<HTTPGenericResponse> {
        let response = await GET(`${PATH}/mandates/${id}`, {});
        return new Promise((resolve) => {
            let generic: HTTPGenericResponse = {
                code: 200,
                status: 'OK',
                message: '',
                result: response
            }
            resolve(generic);
        })
    }

    async function getMandates(offset: number, limit: number, sort: string | null, order: string | null, filterDefs: FilterDef[] = []): Promise<HTTPGenericResponse> {
        let type = getFilterValue(Const.ID_FILTER_TYPE, filterDefs);
        let statuses = getFilterValue(Const.ID_FILTER_STATUS, filterDefs);
        let debtorName = getFilterValue(Const.ID_FILTER_DEBTOR_NAME, filterDefs);
        let debtorIban = getFilterValue(Const.ID_FILTER_DEBTOR_IBAN, filterDefs);
        let subscriberFirstName = getFilterValue(Const.ID_FILTER_SUBSCRIBER_FIRST_NAME, filterDefs);
        let subscriberLastName = getFilterValue(Const.ID_FILTER_SUBSCRIBER_LAST_NAME, filterDefs);
        let subscriberTaxCode = getFilterValue(Const.ID_FILTER_SUBSCRIBER_TAX_CODE, filterDefs);
        let subscriptionDateFrom: Date = getFilterValue(Const.ID_FILTER_SUBSCRIPTION_DATE_FROM, filterDefs);
        let subscriptionDateTo: Date = getFilterValue(Const.ID_FILTER_SUBSCRIPTION_DATE_TO, filterDefs);

        let params = {
            offset: offset,
            limit: limit,
            sortBy: sort,
            order: order,
            type: type,
            status: statuses,
            debtorName: debtorName,
            debtorIban: debtorIban,
            subscriberFirstName: subscriberFirstName,
            subscriberLastName: subscriberLastName,
            subscriberTaxCode: subscriberTaxCode,
            subscriptionDateFrom: subscriptionDateFrom ? dayjs(subscriptionDateFrom).toISOString() : null,
            subscriptionDateTo: subscriptionDateTo ? dayjs(subscriptionDateTo).toISOString() : null,
        }

        let response = await GET(`${PATH}/mandates`, { params: params, paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true }) });
        return new Promise((resolve) => {
            let generic: HTTPGenericResponse = {
                code: 200,
                status: 'OK',
                message: '',
                result: response
            }
            resolve(generic);
        })
    }

    async function postMandate(mandate: any): Promise<HTTPGenericResponse> {
        let response = await POST(`${PATH}/mandates`, mandate);
        return new Promise((resolve) => {
            let generic: HTTPGenericResponse = {
                code: 200,
                status: 'OK',
                message: '',
                result: response
            }
            resolve(generic);
        })
    }

    async function putMandate(mandate: any): Promise<HTTPGenericResponse> {
        let response = await PUT(`${PATH}/mandates/${mandate.id}`, mandate);
        return new Promise((resolve) => {
            let generic: HTTPGenericResponse = {
                code: 200,
                status: 'OK',
                message: '',
                result: response
            }
            resolve(generic)
        })
    }

    async function putMandateStatus(mandate: any): Promise<HTTPGenericResponse> {
        let response = await PUT(`${PATH}/mandates/${mandate.id}/status`, mandate);
        return new Promise((resolve) => {
            let generic: HTTPGenericResponse = {
                code: 200,
                status: 'OK',
                message: '',
                result: response
            }
            resolve(generic);
        })
    }

    async function getCollections(offset: number, limit: number, sort: string | null, order: string | null, filterDefs: FilterDef[] = []): Promise<HTTPGenericResponse> {
        let type = getFilterValue(CollConst.ID_FILTER_REPORT_TYPE, filterDefs);
        let statuses = getFilterValue(CollConst.ID_FILTER_STATUS, filterDefs);
        let collectiontDateFrom: Date = getFilterValue(CollConst.ID_FILTER_COLLECTION_DATE_FROM, filterDefs);
        let collectionDateTo: Date = getFilterValue(CollConst.ID_FILTER_COLLECTION_DATE_TO, filterDefs);
        let reportDateFrom: Date = getFilterValue(CollConst.ID_FILTER_REPORT_DATE_FROM, filterDefs);
        let reportDateTo: Date = getFilterValue(CollConst.ID_FILTER_REPORT_DATE_TO, filterDefs);
        let mandateType = getFilterValue(CollConst.ID_FILTER_MANDATE_TYPE, filterDefs);
        let params = {
            offset: offset,
            limit: limit,
            sortBy: sort,
            order: order,
            type: type,
            receiptDateFrom:  collectiontDateFrom ? dayjs(collectiontDateFrom).toISOString() : null,
            receiptDateTo: collectionDateTo ? dayjs(collectionDateTo).toISOString() : null,
            mandateType: mandateType,
            status: statuses,
            reportDateFrom: reportDateFrom ? dayjs(reportDateFrom).toISOString() : null,
            reportDateTo: reportDateTo ? dayjs(reportDateTo).toISOString() : null,
        }

        let response = await GET(`${PATH}/receipt-reports`, { params: params, paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true }) });
        return new Promise((resolve) => {
            let generic: HTTPGenericResponse = {
                code: 200,
                status: 'OK',
                message: '',
                result: response
            }
            resolve(generic);
        })
    }

    async function getCollectionReports(id : any, offset: number, limit: number, sort: string | null, order: string | null, filterDefs: FilterDef[] = []): Promise<HTTPGenericResponse> {
        let statuses = getFilterValue(CollConst.ID_FILTER_STATUS, filterDefs);
        let collectiontDateFrom: Date = getFilterValue(CollDetailConst.ID_FILTER_SUBSCRIPTION_DATE_FROM, filterDefs);
        let collectionDateTo: Date = getFilterValue(CollDetailConst.ID_FILTER_SUBSCRIPTION_DATE_TO, filterDefs);
        let amountFrom = getFilterValue(CollDetailConst.ID_FILTER_AMOUNT_FROM, filterDefs);
        let amountTo = getFilterValue(CollDetailConst.ID_FILTER_AMOUNT_TO, filterDefs);
        let debtorName = getFilterValue(CollDetailConst.ID_FILTER_DEBTOR_NAME, filterDefs);
        let debtorIban = getFilterValue(CollDetailConst.ID_FILTER_IBAN, filterDefs);
        let mandateExternalId = getFilterValue(CollDetailConst.ID_FILTER_MANDATE_ID, filterDefs);
        let receiptReference = getFilterValue(CollDetailConst.ID_FILTER_REFERENCE, filterDefs);
        let params = {
            offset: offset,
            limit: limit,
            sortBy: sort,
            order: order,
            reportId: id,
            receiptDateFrom:  collectiontDateFrom ? dayjs(collectiontDateFrom).toISOString() : null,
            receiptDateTo: collectionDateTo ? dayjs(collectionDateTo).toISOString() : null,
            amountFrom: amountFrom,
            amountTo: amountTo,
            debtorName: debtorName,
            debtorIban: debtorIban,
            mandateExternalId: mandateExternalId,
            receiptReference: receiptReference,
            status: statuses,
        }

        let response = await GET(`${PATH}/receipt-reports/receipts`, { params: params, paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true }) });
        return new Promise((resolve) => {
            let generic: HTTPGenericResponse = {
                code: 200,
                status: 'OK',
                message: '',
                result: response
            }
            resolve(generic);
        })
    }




    return { getMandate, getMandates, postMandate, putMandate, putMandateStatus, getCollections, getCollectionReports };
}